import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useLoadingStore } from '../../shared/store/loadingStore';
import { maskEmail } from '../../shared/util/sanitize';
import { useOnBoardingData } from '../../shared/hooks/useOnBoardingData';
import { useBulk } from '../../shared/hooks/useApi/useBulk';
import { UpdateImgReq } from '../../shared/dto/ReqDtoRepository';
import { hasSizeData, OnBoardingWorkData } from '../../shared/dto/EntityRepository';
import { getImageDimensions } from '../../shared/util/imageUtils';
import { INPUT_VERIFICATION_CODE_MAX_LENGTH } from '../../shared/constants/limits';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { DimensionsType } from '../../shared/enum/EnumRepository';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const DeferredEmailVerification: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { user, activate, resend } = useAuth();
  const { isLoading } = useLoadingStore();
  const { standardModal, clearModal } = useModalStore();
  const { onBoardingData, clearOnBoardingData } = useOnBoardingData();

  const [verificationCode, setVerificationCode] = useState("");
  const [verificationCodeArray, setVerificationCodeArray] = useState(['', '', '', '', '', '']);
  const [digitsFilled, setDigitsFilled] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { createWorkBulkWithAui } = useBulk();
  const { updateWorkBulkWithAui } = useWorkListActions();

  // 첫 마운트 시 첫번째 input focus
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // 공백 클릭 모달창 닫히는 로직 방지를 위해 입력 값이 있는지 확인
  useEffect(() => {
    if (verificationCode.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [verificationCode]);

  /**
 * 1. 숫자, 영문자만 입력 가능
 * 2. 기존 verificationCode에 새로 들어온 값 추가
 */
  const handleInputChange = (value: string) => {
    if (!/^[a-zA-Z0-9]*$/.test(value)) return;
    setVerificationCode(verificationCode + value);
  };

  /**
   * 1. 6자리 입력 시 마지막 입력창에 focus
   * 2. 6자리 안될 경우 다음 input focus
   * 3. 6자리 입력 여부에 따라 submit btn toggle
   */
  const handleFocus = () => {
    if (verificationCode.length === 6) {
      inputRefs.current[5]?.focus();
      setDigitsFilled(true);
    } else {
      if (digitsFilled === true) {
        setDigitsFilled(false);
      }
      inputRefs.current[verificationCode.length]?.focus();
    }
  }

  // verificationCode 변경에 따라 focus 이동
  // 6자리로 채우고 배열로 변환
  useEffect(() => {
    handleFocus();

    const toCharArr6 = (s: string) =>
      Array.from({ length: 6 }, (_, i) => s[i] ?? '');
    const splited = toCharArr6(verificationCode);
    setVerificationCodeArray(splited);

  }, [verificationCode]);

  useEffect(() => {
    //유저가 생기면, 여기서 유저가 저장한 데이터 업로드
    const uploadWork = async () => {
      if (!user) return;
      if (!onBoardingData) return;

      const works = (onBoardingData.works || []).filter(
        (w): w is OnBoardingWorkData => !!(w as OnBoardingWorkData).file
      ) as OnBoardingWorkData[];

      if (works.length === 0) {
        return;
      }

      const files = works.map((w) => w.file);

      // 이미지 크기를 병렬로 추출 (BulkCreateModal과 동일)
      const imageDimensions = await Promise.all(
        files.map((file) => getImageDimensions(file))
      );

      const results = await createWorkBulkWithAui(user.aui, files);
      if (!results) return;

      const workList = results.map((result, i) => {
        const work = works[i];
        const material = work.material.trim();
        const prodYear = work.prodYear.trim();
        const parsedProdYear = Number(prodYear);
        const hasManualSize = hasSizeData(work.size);
        const isVariable = work.dimensionsType === DimensionsType.VARIABLE;

        return {
          id: result.workId,
          originUrl: result.originUrl,
          width: imageDimensions[i].width,
          height: imageDimensions[i].height,
          title: work.title,
          ...(material ? { material } : {}),
          ...(prodYear && Number.isInteger(parsedProdYear) ? { prodYear: parsedProdYear } : {}),
          ...(isVariable
            ? {
                sizes: [],
                sizeText: '',
                dimensionsType: DimensionsType.VARIABLE,
              }
            : hasManualSize
              ? {
                  mainSizeUnit: work.size.unit,
                  sizes: [work.size],
                  sizeText: '',
                  dimensionsType: DimensionsType.FIXED,
                }
              : {}),
        };
      }) satisfies UpdateImgReq[];

      await updateWorkBulkWithAui(user.aui, { workList });
    }

    const uploadOnBoardingData = async () => {
      if (!user) return;
      if (!onBoardingData) return;
      
      await uploadWork();
      
      clearOnBoardingData();
      window.location.href = `/@${user.handle}/works`;
    }

    uploadOnBoardingData();

  }, [user, onBoardingData]);

  if (standardModal == null) return null;

  /**
   * [키보드 이벤트 처리]
   * 1. 붙여넣기 처리     => verificationCode에 추가 후 6자리까지 자르고 업데이트
   * 2. 백스페이스 처리    => 해당 input 값 삭제 및 이전 input focus
   * 3. 엔터 처리        => 6자리 입력 시 submit
   * 4. 이스케이프 처리    => modal 닫기
   */
  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (isLoading) return;

    // 붙여넣기 처리 (window, mac 모두 지원)
    if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
      event.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const concatText = verificationCode + text;
        const pastedText = concatText.slice(0, 6); // 앞의 6자리만 사용
        setVerificationCode(pastedText);
      });
      return;
    }

    switch (event.key) {
      case 'Backspace':
        if (verificationCode.length > 0) {
          setVerificationCode(verificationCode.slice(0, -1));
        }
        if (!verificationCode[index] && index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
        break;
      case 'Enter':
        handleSubmit();
        break;
      case 'Escape':
        clearModal();
        break;
      default:
        break;
    }
  };

  /**
   * [이메일 인증 요청]
   * 1. 6자리가 안되면 무시
   * 2. 로딩 중이면 무시
   */
  const handleSubmit = async () => {
    if (standardModal.value == null) return;
    if (standardModal.title == null) return;
    if (digitsFilled === false) return;
    if (isLoading > 0) return;

    //여기서는 그냥 코드 확인하고 Active만
    await activate({
      key: standardModal.value,
      verificationCode: verificationCode
    });
  };

  /**
   * [이메일 인증 재요청]
   * 1. 로딩 중이면 무시
   */
  const handleResend = async () => {
    if (standardModal.value == null) return;
    if (isLoading > 0) return;
    await resend({
      key: standardModal.value
    });
    setVerificationCode("");
  }


  return (
    <Container
      tabIndex={-1}
      onKeyDown={(e) => handleKeyDown(e, verificationCode.length)}>
      <Title>{getModalMessage('EMAIL_VERIFY_TITLE')}</Title>

      <InstructionText>
        {getModalMessage('EMAIL_VERIFY_INSTRUCTION')}<br />
        <PrivacyText>
          ({maskEmail(standardModal.value)})
        </PrivacyText>
      </InstructionText>

      <VerificationContainer>
        {verificationCodeArray.map((digit, index) => (
          <DigitInput
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="text"
            maxLength={INPUT_VERIFICATION_CODE_MAX_LENGTH}
            value={digit}
            onChange={(e) => handleInputChange(e.target.value)}
          />
        ))}
      </VerificationContainer>

      <ButtonContainer>
        <SubmitButton onClick={handleSubmit} $disabled={isLoading > 0 || digitsFilled === false}>
          {getBtnName('VERIFY')}
        </SubmitButton>
        <ResendButtonContainer>
          <span>{getModalMessage('EMAIL_VERIFY_NO_CODE')}</span>
          <ResendButton onClick={handleResend}>{getBtnName('RESEND')}</ResendButton>
        </ResendButtonContainer>
      </ButtonContainer>
    </Container>
  );
};


const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  outline: none;
  max-width: 360px;
  margin: 0 auto;
`;

const Title = styled.h2`
  margin-bottom: 44px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;

const InstructionText = styled.p`
  margin-bottom: 24px;
  ${({ theme }) => theme.typography.Body_03_1};
`;

const PrivacyText = styled.span`
  ${({ theme }) => theme.typography.Body_04};
`;

const VerificationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 20px;
`;

const DigitInput = styled.input`
  width: 40px;
  height: 50px;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-radius: 1px;
  ${({ theme }) => theme.typography.Body_02_1};

  &:focus {
    outline: none;
    border: 1.5px solid ${({ theme }) => theme.colors.color_Gray_03};
    border-radius: 2px;
  }
  &::placeholder {
    color: ${({ theme }) => theme.colors.color_Gray_05};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 6px;
`;

const ResendButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  ${({ theme }) => theme.typography.Body_04};
`;

const ResendButton = styled.span`
  font-style: italic;
  text-decoration: underline;
  
  transition: all 0.3s ease-in-out;
  &:hover {
    transform: scale(1.05);
    color: ${({ theme }) => theme.colors.color_Gray_03};
  }
  cursor: pointer;
`;

const SubmitButton = styled.button<{ $disabled: boolean }>`
  width: 100%;
  padding: 10px 0px;
  border: none;
  border-radius: 1px;
  cursor: pointer;
  color: ${({ theme, $disabled }) => $disabled ? theme.colors.color_Gray_06 : theme.colors.color_White};
  background-color: ${({ theme, $disabled }) => $disabled ? theme.colors.color_Gray_03 : theme.colors.color_Gray_01};
  &:hover {
    transform: scale(1.01);
  }
  transition: all 0.2s ease-in-out;
`;


export default DeferredEmailVerification;
